import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { fmtDate } from "@/lib/format";
import { AddressBook, Plus, MagnifyingGlass, PencilSimple, Archive, ArrowCounterClockwise, Copy, MapPin } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const TYPES = ["client", "supplier", "depot", "delivery"];
const EMPTY = { type: "client", name: "", company: "", email: "", phone: "", address: "", lat: null, lng: null, notes: "" };

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [dupWarn, setDupWarn] = useState(null);
  const [geoBusy, setGeoBusy] = useState(false);

  const load = useCallback(async () => {
    const params = { include_archived: includeArchived };
    if (type) params.type = type;
    if (q) params.q = q;
    const { data } = await api.get("/contacts", { params });
    setContacts(data);
  }, [type, q, includeArchived]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditing(null); setDupWarn(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...EMPTY, ...c }); setEditing(c.id); setDupWarn(null); setOpen(true); };

  const save = async () => {
    try {
      if (!editing) {
        const { data } = await api.post("/contacts/check-duplicate", form);
        if (data.duplicate && !dupWarn) { setDupWarn(data.duplicate); return; }
      }
      if (editing) await api.patch(`/contacts/${editing}`, form);
      else await api.post("/contacts", form);
      toast.success(editing ? "Contact updated" : "Contact created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const geocode = async () => {
    if (!form.address) return;
    setGeoBusy(true);
    try {
      const { data } = await api.post("/routing/geocode", { query: form.address });
      if (data.results?.length) {
        const r = data.results[0];
        setForm((f) => ({ ...f, lat: r.lat, lng: r.lng, address: r.label }));
        toast.success("Location pinned");
      } else toast.error("No match found");
    } finally { setGeoBusy(false); }
  };

  const archive = async (c) => { await api.post(`/contacts/${c.id}/archive`); load(); };
  const duplicate = async (c) => { await api.post(`/contacts/${c.id}/duplicate`); toast.success("Duplicated"); load(); };

  return (
    <div>
      <PageHeader title="Contacts" subtitle="Clients, suppliers, depots and delivery points."
        actions={<Button data-testid="new-contact-btn" onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white"><Plus size={18} weight="bold" className="mr-2" />New Contact</Button>} />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="contact-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, company, address…"
            className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-slate-900 outline-none" />
        </div>
        <select data-testid="contact-type-filter" value={type} onChange={(e) => setType(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-md text-sm capitalize bg-white">
          <option value="">All types</option>
          {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} data-testid="show-archived" />
          Archived
        </label>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {contacts.length === 0 ? <EmptyState icon={AddressBook} title="No contacts" hint="Add your first client or depot." /> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left hidden md:table-cell">Company</th>
                <th className="px-4 py-3 text-left hidden lg:table-cell">Address</th>
                <th className="px-4 py-3 text-left hidden sm:table-cell">Phone</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {contacts.map((c) => (
                <tr key={c.id} className={`hover:bg-slate-50 transition-colors ${c.archived ? "opacity-50" : ""}`} data-testid={`contact-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900 flex items-center gap-1.5">{c.lat && <MapPin size={14} className="text-green-600" weight="fill" />}{c.name}</td>
                  <td className="px-4 py-3 capitalize text-slate-600">{c.type}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-slate-600">{c.company || "—"}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-slate-500 max-w-[240px] truncate">{c.address || "—"}</td>
                  <td className="px-4 py-3 hidden sm:table-cell text-slate-600">{c.phone || "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(c)} className="p-1.5 text-slate-400 hover:text-slate-900" title="Edit" data-testid={`edit-contact-${c.id}`}><PencilSimple size={16} /></button>
                    <button onClick={() => duplicate(c)} className="p-1.5 text-slate-400 hover:text-slate-900" title="Duplicate"><Copy size={16} /></button>
                    <button onClick={() => archive(c)} className="p-1.5 text-slate-400 hover:text-slate-900" title={c.archived ? "Restore" : "Archive"}>
                      {c.archived ? <ArrowCounterClockwise size={16} /> : <Archive size={16} />}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit contact" : "New contact"}</DialogTitle></DialogHeader>
          {dupWarn && (
            <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded-r-md text-amber-800 text-sm">
              A contact named "{dupWarn.name}" already exists. Click Save again to create anyway.
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type"><select data-testid="contact-form-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="cd-input capitalize">{TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></Field>
            <Field label="Name"><input data-testid="contact-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="cd-input" /></Field>
            <Field label="Company"><input value={form.company || ""} onChange={(e) => setForm({ ...form, company: e.target.value })} className="cd-input" /></Field>
            <Field label="Phone"><input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="cd-input" /></Field>
            <Field label="Email" full><input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="cd-input" /></Field>
            <Field label="Address" full>
              <div className="flex gap-2">
                <input data-testid="contact-form-address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="cd-input flex-1" />
                <Button type="button" variant="outline" onClick={geocode} disabled={geoBusy} data-testid="geocode-btn"><MapPin size={16} /></Button>
              </div>
              {form.lat && <span className="text-xs text-green-600 font-mono">Pinned: {Number(form.lat).toFixed(4)}, {Number(form.lng).toFixed(4)}</span>}
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="contact-save-btn" onClick={save} className="bg-slate-900 hover:bg-slate-800 text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
